import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import {
  Home, Users, Calendar, Image, FileText, ShoppingBag,
  MessageCircle, Settings, LogOut, Menu, Sun, Moon,
  Shield, MessageSquare, Search, Bell, X, BarChart3, Globe,
} from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_visa-community/artifacts/vrwzubc4_Argo-Atlantic-logo.png';

const Navbar = () => {
  const { user, logout, isApproved, getAuthHeaders } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Search state
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const searchRef = useRef(null);
  const searchTimeout = useRef(null);

  // Notifications state
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);

  const navItems = [
    { href: '/dashboard', label: 'Feed', icon: Home },
    { href: '/groups', label: 'Groups', icon: Users },
    { href: '/discussions', label: 'Discussions', icon: MessageSquare },
    { href: '/events', label: 'Events', icon: Calendar },
    { href: '/media', label: 'Media', icon: Image },
    { href: '/files', label: 'Files', icon: FileText },
    { href: '/marketplace', label: 'Services', icon: ShoppingBag },
  ];

  // Fetch unread notification count
  const fetchUnread = useCallback(async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/notifications/unread-count`, { headers: getAuthHeaders(), withCredentials: true });
      setUnreadCount(res.data.count);
    } catch {}
  }, [user, getAuthHeaders]);

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => clearInterval(interval);
  }, [fetchUnread]);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications/`, { headers: getAuthHeaders(), withCredentials: true });
      setNotifications(res.data);
    } catch {}
  };

  const handleNotifOpen = (open) => {
    setNotifOpen(open);
    if (open) {
      fetchNotifications();
      // Mark all as read
      axios.put(`${API}/notifications/read-all`, {}, { headers: getAuthHeaders(), withCredentials: true })
        .then(() => setUnreadCount(0)).catch(() => {});
    }
  };

  // Search handler with debounce
  const handleSearch = (q) => {
    setSearchQuery(q);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (q.length < 2) { setSearchResults(null); return; }
    setSearching(true);
    searchTimeout.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/search/?q=${encodeURIComponent(q)}`, { headers: getAuthHeaders(), withCredentials: true });
        setSearchResults(res.data);
      } catch {}
      setSearching(false);
    }, 300);
  };

  const closeSearch = () => { setShowSearch(false); setSearchQuery(''); setSearchResults(null); };

  const handleLogout = async () => { await logout(); navigate('/'); };
  const isActive = (path) => location.pathname === path;

  const formatTimeAgo = (dateString) => {
    const d = new Date(dateString);
    const diff = Date.now() - d;
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(diff / 3600000);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(diff / 86400000)}d`;
  };

  const totalResults = searchResults ? Object.values(searchResults).reduce((a, v) => a + v.length, 0) : 0;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/40" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to={user ? '/dashboard' : '/'} className="flex items-center gap-3 flex-shrink-0" data-testid="logo-link">
            <img src={LOGO_URL} alt="Argo Atlantic" className="h-10 w-10 object-contain" />
            <span className="font-serif text-xl font-semibold hidden sm:block">Argo Atlantic</span>
          </Link>

          {/* Desktop Navigation */}
          {user && isApproved && (
            <div className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link key={item.href} to={item.href} data-testid={`nav-${item.label.toLowerCase()}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive(item.href) ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}>
                    <Icon className="h-4 w-4" />{item.label}
                  </Link>
                );
              })}
            </div>
          )}

          {/* Right side */}
          <div className="flex items-center gap-1.5">
            {/* Search */}
            {user && isApproved && (
              <div className="relative" ref={searchRef}>
                {showSearch ? (
                  <div className="flex items-center gap-1">
                    <div className="relative">
                      <Input
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={e => handleSearch(e.target.value)}
                        className="w-40 sm:w-56 h-9 text-sm input-default"
                        autoFocus
                        data-testid="search-input"
                      />
                      {/* Search Results Dropdown */}
                      {searchResults && searchQuery.length >= 2 && (
                        <div className="absolute top-full mt-1 w-72 sm:w-80 right-0 bg-card border border-border rounded-lg shadow-lg z-50 overflow-hidden">
                          <ScrollArea className="max-h-80">
                            {totalResults === 0 ? (
                              <p className="p-4 text-sm text-muted-foreground text-center">No results found</p>
                            ) : (
                              <div className="p-2 space-y-1">
                                {searchResults.posts?.length > 0 && (
                                  <div>
                                    <p className="text-xs text-muted-foreground px-2 py-1 font-medium">Posts</p>
                                    {searchResults.posts.slice(0, 3).map(p => (
                                      <div key={p.post_id} className="px-2 py-1.5 hover:bg-muted/50 rounded cursor-pointer text-sm truncate" onClick={() => { closeSearch(); navigate('/dashboard'); }}>
                                        {p.content?.substring(0, 60)}...
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {searchResults.discussions?.length > 0 && (
                                  <div>
                                    <p className="text-xs text-muted-foreground px-2 py-1 font-medium">Discussions</p>
                                    {searchResults.discussions.slice(0, 3).map(d => (
                                      <div key={d.discussion_id} className="px-2 py-1.5 hover:bg-muted/50 rounded cursor-pointer text-sm truncate" onClick={() => { closeSearch(); navigate('/discussions'); }}>
                                        {d.title}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {searchResults.services?.length > 0 && (
                                  <div>
                                    <p className="text-xs text-muted-foreground px-2 py-1 font-medium">Services</p>
                                    {searchResults.services.slice(0, 3).map(s => (
                                      <div key={s.service_id} className="px-2 py-1.5 hover:bg-muted/50 rounded cursor-pointer text-sm truncate" onClick={() => { closeSearch(); navigate('/marketplace'); }}>
                                        {s.title} - ${s.price}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {searchResults.groups?.length > 0 && (
                                  <div>
                                    <p className="text-xs text-muted-foreground px-2 py-1 font-medium">Groups</p>
                                    {searchResults.groups.slice(0, 3).map(g => (
                                      <div key={g.group_id} className="px-2 py-1.5 hover:bg-muted/50 rounded cursor-pointer text-sm truncate" onClick={() => { closeSearch(); navigate('/groups'); }}>
                                        {g.name}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {searchResults.users?.length > 0 && (
                                  <div>
                                    <p className="text-xs text-muted-foreground px-2 py-1 font-medium">Users</p>
                                    {searchResults.users.slice(0, 3).map(u => (
                                      <div key={u.user_id} className="px-2 py-1.5 hover:bg-muted/50 rounded cursor-pointer text-sm truncate flex items-center gap-2">
                                        <Avatar className="h-5 w-5"><AvatarFallback className="text-[9px]">{u.name?.charAt(0)}</AvatarFallback></Avatar>
                                        {u.name} <span className="text-xs text-muted-foreground capitalize">({u.role})</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </ScrollArea>
                        </div>
                      )}
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={closeSearch}><X className="h-4 w-4" /></Button>
                  </div>
                ) : (
                  <Button variant="ghost" size="icon" className="rounded-full" onClick={() => setShowSearch(true)} data-testid="search-toggle">
                    <Search className="h-5 w-5" />
                  </Button>
                )}
              </div>
            )}

            {/* Language switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full" data-testid="language-switcher">
                  <Globe className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {[{code:'en',label:'English'},{code:'es',label:'Espanol'},{code:'hi',label:'हिन्दी'},{code:'fr',label:'Francais'}].map(lang => (
                  <DropdownMenuItem key={lang.code} onClick={() => { i18n.changeLanguage(lang.code); localStorage.setItem('language', lang.code); }}
                    className={i18n.language === lang.code ? 'bg-primary/10 text-primary' : ''}>
                    {lang.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Theme toggle */}
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle" className="rounded-full">
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>

            {user ? (
              <>
                {/* Notifications Bell */}
                <Popover open={notifOpen} onOpenChange={handleNotifOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="icon" className="rounded-full relative" data-testid="notifications-btn">
                      <Bell className="h-5 w-5" />
                      {unreadCount > 0 && (
                        <Badge className="absolute -top-1 -right-1 h-4 min-w-4 p-0 flex items-center justify-center text-[10px]" data-testid="unread-badge">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </Badge>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-0" align="end">
                    <div className="p-3 border-b border-border">
                      <h3 className="font-semibold text-sm">Notifications</h3>
                    </div>
                    <ScrollArea className="max-h-80">
                      {notifications.length === 0 ? (
                        <p className="p-4 text-sm text-muted-foreground text-center">No notifications</p>
                      ) : (
                        <div className="p-1">
                          {notifications.map(n => (
                            <div key={n.notification_id} className={`p-3 rounded-lg cursor-pointer transition-colors hover:bg-muted/50 ${!n.is_read ? 'bg-primary/5' : ''}`}
                              onClick={() => { setNotifOpen(false); if (n.link) navigate(n.link); }}
                              data-testid={`notif-${n.notification_id}`}>
                              <p className="text-sm font-medium">{n.title}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">{n.message}</p>
                              <p className="text-xs text-muted-foreground mt-1">{formatTimeAgo(n.created_at)} ago</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </PopoverContent>
                </Popover>

                {/* Messages */}
                <Link to="/messages" data-testid="messages-link">
                  <Button variant="ghost" size="icon" className="rounded-full"><MessageCircle className="h-5 w-5" /></Button>
                </Link>

                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="user-menu-trigger">
                      <Avatar className="h-10 w-10 border-2 border-primary/20">
                        <AvatarImage src={user.picture} alt={user.name} />
                        <AvatarFallback className="bg-primary/10 text-primary">{user.name?.charAt(0)?.toUpperCase()}</AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="flex items-center gap-2 p-2">
                      <Avatar className="h-8 w-8"><AvatarImage src={user.picture} /><AvatarFallback>{user.name?.charAt(0)?.toUpperCase()}</AvatarFallback></Avatar>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{user.name}</span>
                        <span className="text-xs text-muted-foreground capitalize">{user.role}</span>
                      </div>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild><Link to="/profile" className="cursor-pointer" data-testid="profile-link"><Settings className="mr-2 h-4 w-4" />Profile Settings</Link></DropdownMenuItem>
                    {user.role === 'admin' && (
                      <>
                        <DropdownMenuItem asChild><Link to="/admin" className="cursor-pointer" data-testid="admin-link"><Shield className="mr-2 h-4 w-4" />Admin Panel</Link></DropdownMenuItem>
                        <DropdownMenuItem asChild><Link to="/analytics" className="cursor-pointer" data-testid="analytics-link"><BarChart3 className="mr-2 h-4 w-4" />Analytics</Link></DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive" data-testid="logout-btn"><LogOut className="mr-2 h-4 w-4" />Logout</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Mobile menu */}
                <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                  <SheetTrigger asChild className="lg:hidden"><Button variant="ghost" size="icon"><Menu className="h-5 w-5" /></Button></SheetTrigger>
                  <SheetContent side="right" className="w-72">
                    <div className="flex flex-col gap-4 mt-8">
                      {navItems.map((item) => {
                        const Icon = item.icon;
                        return (
                          <Link key={item.href} to={item.href} onClick={() => setMobileMenuOpen(false)}
                            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                              isActive(item.href) ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                            }`}>
                            <Icon className="h-5 w-5" />{item.label}
                          </Link>
                        );
                      })}
                    </div>
                  </SheetContent>
                </Sheet>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login"><Button variant="ghost" data-testid="login-nav-btn">Sign In</Button></Link>
                <Link to="/register"><Button className="btn-primary" data-testid="register-nav-btn">Get Started</Button></Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
